import { IEncodedPersistedAction } from '@engoo/realtime-protocol/persisted';
import RealtimeError from 'app/services/rtc/RealtimeError';
import { ActionStreamEvents, IActionStream, IMetadata } from 'app/services/rtc/sync/ActionStream';
import { AbstractDocumentManager, RETRY_INTERVAL_MS } from 'app/services/rtc/sync/documentmanagers/base';
import { ActionState, Events, IDocumentManager } from 'app/services/rtc/sync/documentmanagers/index';
import { ConditionVariable } from 'app/utils/ConditionVariable';
import { indexBy } from 'app/utils/immutable';
import { sleep } from 'app/utils/async';
import { Map } from 'immutable';

export interface IActionHandler<T, ActionT> {
  apply(document: T, x: ActionT, state: ActionState): T;
}

export interface IRetryAction<ActionT> {
  action: ActionT;
  onDone: <A extends ActionT>(persistedAction: A & IEncodedPersistedAction) => void;
  onError: (...args: any[]) => void;
}

// latest snapshot = base snapshot + inflight + ahead
//    ahead might be from me, or might be from someone else
//      they're committed actions that can't be rolled into
//      base because something might be ordered before them
export class LWWDocumentManager<DocumentT, ActionT>
  extends AbstractDocumentManager<DocumentT, ActionT>
  implements IDocumentManager<DocumentT, ActionT> {
  // Actions that have yet to be persisted.
  // Either on the wire or on the queue to be retried
  private localActions: ActionT[] = [];

  // Failed
  private retryQueue: Array<IRetryAction<ActionT>> = [];

  private requestedShutdown = false;

  private cvRetryAction = new ConditionVariable();

  // Combination of:
  //   - foreign actions received out of order
  //   - submitted and confirmed actions that are ahead of the snapshot
  private aheadActions: Map<number, ActionT & IEncodedPersistedAction> =
            Map<number, ActionT & IEncodedPersistedAction>();

  public constructor(
    private readonly actionHandler: IActionHandler<DocumentT, ActionT>,
    actionStream: IActionStream<ActionT>,
    snapshot: DocumentT,
    metadata: IMetadata | undefined,
    snapshotVersion: number = -1,
  ) {
    super(actionStream, snapshot, metadata, snapshotVersion);
  }

  public start() {
    super.start();

    this.runRetrySubmitActionThread();

    this.onAction = this.onAction.bind(this);
    this.actionStream.on(ActionStreamEvents.ACTION, this.onAction);
  }

  public onAction(x: ActionT & IEncodedPersistedAction): void {
    this.onActions([x]);
  }

  public onActions(actions: Array<IEncodedPersistedAction & ActionT>): void {
    this.integrateActions(actions);

    this.emit(Events.REMOTE_UPDATED, {
      actions,
      state: this.latestSnapshot,
    });
  }

  public async addLocalActionWithResult<A extends ActionT>(
    action: A,
  ): Promise<A & IEncodedPersistedAction> {
    this.localActions.push(action);
    this.latestSnapshot =
      this.actionHandler.apply(this.latestSnapshot, action, ActionState.Pending);

    this.emit(Events.CHANGED, {
      actions: [action],
      state:   this.latestSnapshot,
    });

    let persisted;
    try {
      persisted = await this.actionStream.append(action);
    } catch (e) {
      if ((e instanceof RealtimeError) && e.shouldRetry()) {
        return await this.retryAction(action);
      } else {
        // Drop the action
        this.localActions = this.localActions.filter((x) => x !== action);
        this.integrateActions([]);
        throw e;
      }
    }

    this.actionSubmitted(action, persisted);
    return persisted;
  }

  public async addLocalAction(action: ActionT): Promise<void> {
    await this.addLocalActionWithResult(action);
  }

  public shutdown() {
    this.actionStream.removeListener(ActionStreamEvents.ACTION, this.onAction);

    this.requestedShutdown = true;
    this.signalAll();

    super.shutdown();
  }

  private retryAction<A extends ActionT>(
    action: A,
  ): Promise<A & IEncodedPersistedAction> {
    return new Promise((resolve, reject) => {
      const retryAction: IRetryAction<A> = { action, onDone: resolve, onError: reject };

      this.retryQueue.push(retryAction);
      this.cvRetryAction.signal();
    });
  }

  private signalAll() {
    this.cvRetryAction.signal();
  }

  private async runRetrySubmitActionThread(): Promise<void> {
    while (!this.requestedShutdown) {
      if (this.retryQueue.length === 0) {
        await this.cvRetryAction.wait();
        continue;
      }

      const { action, onDone, onError } = this.retryQueue.shift()!;
      let persisted;
      try {
        persisted = await this.actionStream.append(action);
      } catch (e) {
        if ((e instanceof RealtimeError) && e.shouldRetry()) {
          this.retryQueue.unshift({ action, onDone, onError });
          await sleep(RETRY_INTERVAL_MS);
        } else {
          onError(e);
        }

        continue;
      }

      this.actionSubmitted(action, persisted);
      onDone(persisted);
    }
  }

  private actionSubmitted(original: ActionT, persisted: ActionT & IEncodedPersistedAction): void {
    const inFlightIndex = this.localActions.indexOf(original);
    if (inFlightIndex === -1) {
      throw new Error('integrating submitted event: event not found in inflightActions');
    }

    this.localActions.splice(inFlightIndex, 1);
    this.integrateActions([persisted]);
  }

  private computeLatest(): DocumentT {
    let doc = this.snapshot;

    const applyAs = (state: ActionState) => (d: DocumentT, action: ActionT) =>
      this.actionHandler.apply(d, action, state);

    // Here's an interesting point: the ordering of these is necessarily unknown
    // Suppose:
    //   we have version t=39
    //   we submit action a1 which will eventually be placed at t=42
    // Then
    //   receive action at t=41
    //   receive action at t=43
    // So
    //   inflight = [a1]
    //   ahead = { 41: a2, 43: a3 }
    //   the correct ordering is [a2, a1, a3]
    //   but we will apply as [a2, a3, a1]
    // Which is wrong.
    // However, inflight is *probably* going to be after aheadActions.
    // Of course this will all be resolved as soon as our submission is confirmed and the
    // canonical global order is established.
    //
    // We could avoid this by only receiving in global order and ignoring 'ahead' here
    // and force head-of-line blocking.

    doc = this.aheadActions.sortBy((value, key) => key).reduce(applyAs(ActionState.Confirmed), doc);
    doc = this.localActions.reduce(applyAs(ActionState.Pending), doc);

    return doc;
  }

  private integrateActions(newActions: Array<ActionT & IEncodedPersistedAction>) {
    // History backfill might race against new actions, and cause actions that are already
    // integrated to be presented as new again.
    const newActionsFiltered = newActions.filter((x) => x.position > this.snapshotVersion);

    // Unify the pool of ahead actions, from existing ones and the incoming ones
    let aheadActions = this.aheadActions.merge(indexBy(newActionsFiltered, (x) => x.position));

    // Apply all continguous ahead actions
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const nextVersion = this.snapshotVersion + 1;

      if (!aheadActions.has(nextVersion)) {
        break;
      }

      const nextAction = aheadActions.get(nextVersion)!;
      aheadActions     = aheadActions.delete(nextVersion);

      this.snapshot = this.actionHandler.apply(this.snapshot, nextAction, ActionState.Confirmed);
      this.snapshotVersion = nextVersion;
    }

    // Buffer the remaining actions, after the discontiguity.
    this.aheadActions = aheadActions;

    // We have a new action, something will have changed in the latestSnapshot.
    this.latestSnapshot = this.computeLatest();

    this.emit(Events.CHANGED, {
      actions: newActionsFiltered,
      state:   this.latestSnapshot,
    });
  }
}
